<template>
    <AxRoadPage :title="$t('truckDetails')" no-padding>
        <div v-if="truckDetails" class="truck-details">
            <div class="truck-details__picture">
                <img
                    :src="
                        truckDetails.picture ||
                            assetTypePicture ||
                            'https://place-hold.it/375x200'
                    "
                    alt="Truck Picture"
                    class="truck-details__img"
                />
            </div>
            <div class="l-stack l-gap-3 l-padded-y">
                <TruckDetailsWidget :tracker="tracker" />
                <LivePositionToggle
                    v-if="!truckDetails.static"
                    class="truck-details__section truck-details__section--horizontal"
                    :tracker-id="tracker.id"
                />
                <TrailerDetailsWidget :trailer-asset="trailerAsset" />
                <VSpinner
                    v-if="isDriverFetching"
                    size="medium"
                    line-fg-color="#000"
                    :speed="1"
                />
                <template v-else>
                    <DriverDetailsWidget
                        v-if="
                            driver &&
                                truckDetails.sensor_data
                                    .driver_1_card_presence &&
                                truckDetails.sensor_data.driver_1_card_presence
                                    .value === 1
                        "
                        :driver="driver"
                    />
                    <div
                        v-else-if="
                            truckDetails.sensor_data.driver_name &&
                                truckDetails.sensor_data.driver_1_card_presence
                                    .value === 1
                        "
                        class="truck-details__section"
                    >
                        <div class="truck-details__title">
                            <DriverAvatar
                                :title="
                                    truckDetails.sensor_data.driver_name.value
                                "
                            />
                            <span>{{
                                truckDetails.sensor_data.driver_name.value
                            }}</span>
                        </div>
                    </div>
                </template>
                <div class="truck-details__section">
                    <h1 class="truck-details__title">
                        <LocationTargetIcon width="24" height="24" />
                        {{ $t('cockpit') }}
                    </h1>
                    <div class="l-inline l-gap-2">
                        <InfoBlock
                            v-if="truckDetails.sensor_data.fuel_level"
                            :label="$t('shared.measurements.fuel_level')"
                            :value="truckDetails.sensor_data.fuel_level.value"
                            class="l-half-width"
                            outlined
                            variant="diagram"
                        >
                            <template #icon>
                                <FuelCanIcon width="24" height="24" />
                            </template>
                        </InfoBlock>
                        <InfoBlock
                            v-if="truckDetails.sensor_data.fuel_rate"
                            :label="$t('shared.measurements.fuel_rate')"
                            :value="
                                `${truckDetails.sensor_data.fuel_rate.value} l/h`
                            "
                            class="l-half-width"
                            outlined
                            variant="centered"
                        >
                            <template #icon>
                                <FuelCanIcon width="24" height="24" />
                            </template>
                        </InfoBlock>
                    </div>
                    <div class="l-inline l-gap-2">
                        <InfoBlock
                            v-if="truckDetails.sensor_data.fuel_level_liters"
                            :label="$t('shared.measurements.fuel_level_liters')"
                            :value="
                                `${truckDetails.sensor_data.fuel_level_liters.value} L`
                            "
                            class="l-half-width"
                            outlined
                            variant="centered"
                        >
                            <template #icon>
                                <FuelCanIcon width="24" height="24" />
                            </template>
                        </InfoBlock>
                        <InfoBlock
                            v-if="truckDetails.sensor_data.odometer"
                            :label="$t('kmTotal')"
                            :value="
                                formatDistance(
                                    truckDetails.sensor_data.odometer.value
                                )
                            "
                            class="l-half-width"
                            outlined
                            variant="centered"
                        >
                            <template #icon>
                                <OdometerIcon width="24" height="24" />
                            </template>
                        </InfoBlock>
                    </div>
                </div>
                <div
                    v-if="
                        additionalData.length &&
                            additionalData.filter(f => f.value).length
                    "
                    class="truck-details__section truck-details__section"
                >
                    <h1 class="truck-details__title">
                        <InfoIcon width="24" height="24" />
                        <span>{{ $t('customerFields') }}</span>
                    </h1>
                    <div class="l-stack l-gap-2">
                        <CustomerField
                            v-for="(field, index) in additionalData"
                            :key="index"
                            :field="field"
                        />
                    </div>
                </div>
                <WarningsWidget
                    :entity-id="truckDetails.id"
                    entity-type="truck"
                />
            </div>
        </div>
    </AxRoadPage>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import AssetHelpers from '@/mixins/AssetHelpers'
import AxRoadPage from '@/components/ax-road/AxRoadPage'
import DriverDetailsWidget from '@/components/ax-road/DriverDetailsWidget'
import DriverAvatar from '@/components/ax-road/DriverAvatar'
import FuelCanIcon from '@/components/icons/FuelCanIcon'
import InfoBlock from '@/components/ax-road/InfoBlock'
import LivePositionToggle from '@/components/ax-road/LivePositionToggle'
import LocationTargetIcon from '@/components/icons/LocationTargetIcon'
import OdometerIcon from '@/components/icons/OdometerIcon'
import TrailerDetailsWidget from '@/components/ax-road/TrailerDetailsWidget'
import TruckDetailsWidget from '@/components/ax-road/TruckDetailsWidget'
import WarningsWidget from '@/components/ax-road/WarningsWidget.vue'
import CustomerField from '@/components/CustomerField.vue'
import InfoIcon from '@/components/icons/InfoIcon.vue'
import { httpHelper } from '@/utils'
import VSpinner from 'vue-simple-spinner'

export default {
    name: 'TruckItemView',
    components: {
        VSpinner,
        DriverAvatar,
        WarningsWidget,
        AxRoadPage,
        DriverDetailsWidget,
        FuelCanIcon,
        InfoBlock,
        LocationTargetIcon,
        OdometerIcon,
        TrailerDetailsWidget,
        TruckDetailsWidget,
        CustomerField,
        InfoIcon,
        LivePositionToggle,
    },
    mixins: [AssetHelpers],
    data() {
        return {
            tracker: null,
            truckDetails: null,
            driver: null,
            driverName: '',
            isDriverFetching: false,
        }
    },
    computed: {
        ...mapGetters('tracker', ['assetsById', 'assetTypesById']),
        assetTypePicture() {
            return this.assetTypesById[this.truckDetails?.asset_type]?.picture
                ?.url
        },
        trailerAsset() {
            return this.tracker?.current_trailer
        },
        additionalData() {
            return this.getCustomerData(this.tracker).filter(
                field =>
                    field.value !== undefined &&
                    field.value !== null &&
                    field.value !== ''
            )
        },
    },
    watch: {
        '$route.params.id': {
            handler(id) {
                this.fetchTruckDetails()

                // clear any previous watcher first if needed
                if (this._assetUnwatch) this._assetUnwatch()

                // watch dynamically chosen key
                this._assetUnwatch = this.$watch(
                    () => this.assetsById[id],
                    () => {
                        this.fetchDriverDetails()
                    },
                    { deep: true }
                )
            },
            immediate: true,
        },
    },
    beforeRouteLeave(to, from, next) {
        this.setActiveTrackerOnMap(null)
        if (this._assetUnwatch) {
            this._assetUnwatch()
        }
        next()
    },
    methods: {
        ...mapMutations('tracker', ['setActiveTrackerOnMap', 'setDriverById']),
        formatDistance(distance) {
            return distance
                ? `${(distance / 1000).toFixed(2)} ${this.$t(
                      'shared.units.km'
                  )}`
                : '-'
        },
        async fetchDriverDetails() {
            const driverId = this.truckDetails.sensor_data.driver_id?.value

            if (driverId) {
                this.isDriverFetching = true
                const { data } = await httpHelper.get(
                    `/tachograph/driver-details/${driverId}`
                )

                this.driver = data
                this.setDriverById({ id: driverId, data })
                this.isDriverFetching = false
            }
        },
        fetchTruckDetails() {
            this.tracker = this.assetsById[this.$route.params.id]
            this.truckDetails = this.tracker.asset_details

            this.fetchDriverDetails()
            this.setActiveTrackerOnMap(null)

            this.$nextTick(() => {
                if (this.tracker && this.tracker.asset_details) {
                    this.setActiveTrackerOnMap(this.tracker)
                }
            })
        },
    },
}
</script>

<i18n>
{
    "en" : {
        "truckDetails": "Truck Details",
        "truckNumber": "Truck number",
        "licensePlate": "License plate",
        "payload": "Payload",
        "totalWeight": "Total weight",
        "drawWeight": "Draw weight",
        "emptyWeight": "Empty weight",
        "route": "Route",
        "kmTotal": "Km Total",
        "trailer": "Trailer",
        "trailerNumber": "Trailer number",
        "details": "Details",
        "warnings": "Warnings",
        "customerFields": "Customer defined fields",
        "cockpit": "Cockpit"
    },
    "de" : {
        "truckDetails": "Fahrzeugsdetail",
        "truckNumber": "Lastwagennummer",
        "licensePlate": "Kontrollschild",
        "payload": "Nutzlast",
        "totalWeight": "Gesamtgewicht",
        "drawWeight": "Zuggewicht",
        "emptyWeight": "Leergewicht",
        "route": "Fahrtenverlauf",
        "kmTotal": "Km Total",
        "trailer": "Anhänger",
        "trailerNumber": "Anhängernummer",
        "details": "Details",
        "warnings": "Warnungen",
        "customerFields": "Benutzerdefinierte Felder",
        "cockpit": "Cockpit"
    },
    "fr" : {
        "truckDetails": "Détails du camion",
        "truckNumber": "Numéro de camion",
        "licensePlate": "Plaque d'immatriculation",
        "payload": "Charge utile",
        "totalWeight": "Poids total",
        "drawWeight": "Poids tracté",
        "emptyWeight": "Poids à vide",
        "route": "Itinéraire",
        "kmTotal": "Km Total",
        "trailer": "Remorque",
        "trailerNumber": "Numéro de remorque",
        "details": "Détails",
        "warnings": "Avertissements",
        "customerFields": "Champs définis par le client",
        "cockpit": "Cockpit"
    },
    "it": {
        "truckDetails": "Dettagli del camion",
        "truckNumber": "Numero del camion",
        "licensePlate": "Targa",
        "payload": "Carico utile",
        "totalWeight": "Peso totale",
        "drawWeight": "Peso trainato",
        "emptyWeight": "Peso a vuoto",
        "route": "Percorso",
        "kmTotal": "Km Totali",
        "trailer": "Rimorchio",
        "trailerNumber": "Numero del rimorchio",
        "details": "Dettagli",
        "warnings": "Avvisi",
        "customerFields": "Campi definiti dal cliente",
        "cockpit": "Cockpit"
    }
}
</i18n>

<style lang="scss">
.truck-details {
    &__img {
        width: 100%;
    }

    &__title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 700;
        line-height: 20px;

        &--padded {
            padding: 0 24px;
        }
    }

    &__icon {
        filter: invert(1) brightness(0);
    }

    &__ago {
        flex-grow: 1;
        text-align: right;
        font-weight: normal;
        color: $color-gray;
        font-size: 13px;
        white-space: nowrap;
    }

    &__section {
        padding: 0 24px 24px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        border-bottom: $style-border;

        &--nopadding {
            padding: 0;
        }
        &--horizontal {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
        }
    }
}
</style>
